import { useCallback, useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { POST_TYPES } from '../lib/cms'
import { logoSrc } from '../data/content'
import AdminWelcome, { type AdminWelcomeState } from './AdminWelcome'
import { api } from './api'
import { AdminRoleContext, type AdminRole } from './role'

const itemClass = ({ isActive }: { isActive: boolean }) =>
  `block px-4 py-2 text-[13px] ${isActive ? 'bg-rose text-white' : 'text-white/75 hover:bg-white/10 hover:text-white'}`

const subClass = ({ isActive }: { isActive: boolean }) =>
  `block py-1.5 pl-8 pr-4 text-[12px] ${isActive ? 'text-white' : 'text-white/55 hover:text-white'}`

export default function AdminShell() {
  const [ready, setReady] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AdminRole>('editor')
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const welcome = location.state as AdminWelcomeState | null
  const showWelcome = Boolean(welcome?.welcome && welcome.greeting)
  const finishWelcome = useCallback(() => {
    navigate(location.pathname, { replace: true, state: {} })
  }, [navigate, location.pathname])

  useEffect(() => {
    document.title = 'TIGG Admin'
    api<{ email: string; role?: AdminRole }>('/api/admin/me')
      .then((me) => {
        setEmail(me.email)
        setRole(me.role === 'admin' ? 'admin' : 'editor')
        setReady(true)
      })
      .catch(() => {
        navigate('/admin/login', { replace: true })
      })
  }, [navigate])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  async function logout() {
    await api('/api/admin/logout', { method: 'POST' })
    navigate('/admin/login')
  }

  if (!ready && !showWelcome) {
    return <div className="grid min-h-svh place-items-center bg-paper text-[11px] tracking-[0.2em] text-muted uppercase">Loading</div>
  }

  const sidebar = (
    <div className="flex h-full min-h-0 flex-col">
      <Link to="/admin" aria-label="TIGG Studio" className="border-b border-white/10 px-4 py-5">
        <img
          src={logoSrc}
          alt="TIGG"
          width={160}
          height={42}
          className="block h-8 w-auto brightness-0 invert"
        />
      </Link>
      <nav className="min-h-0 flex-1 overflow-y-auto py-3">
        <NavLink to="/admin" end className={itemClass}>
          Dashboard
        </NavLink>

        <p className="text-caps mt-5 mb-1 px-4 text-[9px] tracking-[0.18em] text-rose">Post types</p>
        {POST_TYPES.map((type) => {
          const open = location.pathname === `/admin/${type.slug}` || location.pathname.startsWith(`/admin/${type.slug}/`)
          return (
            <div key={type.slug} className={open ? 'bg-white/5' : undefined}>
              <NavLink to={`/admin/${type.slug}`} className={itemClass}>
                {type.menu}
              </NavLink>
              {open ? (
                <div className="pb-2">
                  <NavLink to={`/admin/${type.slug}`} end className={subClass}>
                    {type.allLabel}
                  </NavLink>
                  <NavLink to={`/admin/${type.slug}/new`} className={subClass}>
                    {type.addLabel}
                  </NavLink>
                </div>
              ) : null}
            </div>
          )
        })}

        {role === 'admin' ? (
          <>
            <p className="text-caps mt-5 mb-1 px-4 text-[9px] tracking-[0.18em] text-rose">People</p>
            <div className={location.pathname.startsWith('/admin/contributors') ? 'bg-white/5' : undefined}>
              <NavLink to="/admin/contributors" className={itemClass}>
                Contributors
              </NavLink>
              {location.pathname.startsWith('/admin/contributors') ? (
                <div className="pb-2">
                  <NavLink to="/admin/contributors" end className={subClass}>
                    All Contributors
                  </NavLink>
                  <NavLink to="/admin/contributors/new" className={subClass}>
                    Add New
                  </NavLink>
                </div>
              ) : null}
            </div>
            <NavLink to="/admin/team" className={itemClass}>
              Team
            </NavLink>

            <p className="text-caps mt-5 mb-1 px-4 text-[9px] tracking-[0.18em] text-rose">Site</p>
            <NavLink to="/admin/stats" className={itemClass}>
              Site statistics
            </NavLink>
            <NavLink to="/admin/newsletter" className={itemClass}>
              Newsletter
            </NavLink>
            <NavLink to="/admin/health" className={itemClass}>
              Site Health
            </NavLink>
            <NavLink to="/admin/logs" className={itemClass}>
              Logs
            </NavLink>
          </>
        ) : (
          <p className="text-caps mt-5 mb-1 px-4 text-[9px] tracking-[0.18em] text-rose">Site</p>
        )}
        <a href="/" target="_blank" rel="noreferrer" className="block px-4 py-2 text-[13px] text-white/75 hover:bg-white/10 hover:text-white">
          View site
        </a>
      </nav>
      <div className="border-t border-white/10 px-4 py-4">
        <p className="truncate text-[11px] text-white/45">{email}</p>
        <p className="text-caps mt-0.5 text-[9px] tracking-[0.18em] text-white/35">{role}</p>
        <div className="mt-2 flex gap-4">
          <Link to="/admin/account" className="text-[12px] text-white/70 hover:text-white">
            Account
          </Link>
          <button type="button" onClick={() => void logout()} className="text-[12px] text-white/70 hover:text-white">
            Log out
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-svh bg-sand lg:flex">
      {showWelcome && welcome ? (
        <AdminWelcome greeting={welcome.greeting} startedAt={welcome.startedAt} ready={ready} onDone={finishWelcome} />
      ) : null}
      <aside className="hidden w-60 shrink-0 bg-ink lg:block">{sidebar}</aside>

      {menuOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button type="button" aria-label="Close menu" className="absolute inset-0 bg-ink/40" onClick={() => setMenuOpen(false)} />
          <aside className="relative h-full w-60 bg-ink">{sidebar}</aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-rule bg-paper px-4 py-3 lg:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="text-caps text-[11px] text-ink"
            aria-label="Open menu"
          >
            Menu
          </button>
          <img src={logoSrc} alt="TIGG" width={120} height={32} className="block h-7 w-auto" />
        </header>
        <main className="min-w-0 flex-1 bg-paper px-5 py-8 sm:px-8 lg:px-10">
          <AdminRoleContext.Provider value={role}>
            <Outlet />
          </AdminRoleContext.Provider>
        </main>
      </div>
    </div>
  )
}
